import {
  assertSome,
  isSome,
  Maybe,
  noop,
  useForwardedRef,
} from '@crocodille/common';
import React, {
  cloneElement,
  createContext,
  ForwardedRef,
  forwardRef,
  ReactElement,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsomorphicLayoutEffect as useLayoutEffect } from './use-isomorphic-layout-effect';

class IntersectionObserverContextValue {
  private readonly observer: IntersectionObserver;
  private readonly payloads = new Map<Element, RefObject<unknown>>();

  constructor(
    public readonly rootRef: RefObject<Element | Document | null> | null,
    private readonly callback: (
      entries: IntersectionObserverEntryWithPayload[],
    ) => void,
    options: Omit<IntersectionObserverInit, 'root'> = {},
  ) {
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        root: rootRef?.current ?? null,
        ...options,
      },
    );
  }

  private handleIntersection(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      const payloadRef = this.payloads.get(entry.target);

      if (payloadRef) {
        (entry as unknown as { payload: unknown }).payload = payloadRef.current;
      }
    }

    this.callback(entries);
  }

  public observe(
    targetRef: RefObject<Element | null>,
    payloadRef?: RefObject<unknown>,
  ) {
    const target = assertSome(targetRef.current);

    if (isSome(payloadRef)) {
      this.payloads.set(target, payloadRef);
    }

    this.observer.observe(target);
  }

  public unobserve(targetRef: RefObject<Element | null>) {
    const target = assertSome(targetRef.current);

    this.payloads.delete(target);

    this.observer.unobserve(target);
  }

  public disconnect() {
    this.observer.disconnect();
  }
}

const IntersectionObserverContext =
  createContext<Maybe<IntersectionObserverContextValue>>(null);

export const IntersectionObserverProvider: React.FC<
  React.PropsWithChildren<
    Omit<IntersectionObserverInit, 'root'> & {
      readonly root?: RefObject<Element | Document | null> | null;
      readonly callback: (
        entries: IntersectionObserverEntryWithPayload[],
      ) => void;
    }
  >
> = ({ root, children, rootMargin, threshold, callback }) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const [observer, setObserver] =
    useState<Maybe<IntersectionObserverContextValue>>(null);

  useLayoutEffect(() => {
    const contextValue = new IntersectionObserverContextValue(
      root ?? null,
      (entries) => callbackRef.current(entries),
      { rootMargin, threshold },
    );

    setObserver(contextValue);

    return () => {
      contextValue.disconnect();
    };
  }, [root, rootMargin, threshold]);

  return (
    <IntersectionObserverContext.Provider value={observer}>
      {children}
    </IntersectionObserverContext.Provider>
  );
};

export interface IntersectionObserverEntryWithPayload
  extends IntersectionObserverEntry {
  readonly payload?: unknown;
}

export interface IntersectionObserverContainerProps
  extends Omit<IntersectionObserverInit, 'root'> {
  /**
   * Allows only for a single child. The container to observe.
   */
  children: ReactElement<
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
  >;
  /**
   * Callback given to intersection observer.
   */
  callback(entries: IntersectionObserverEntryWithPayload[]): void;
}

/**
 * Wraps a single html element and instantiates an intersection observer with it as root.
 *
 * @see useIntersectionObserver
 */
export const IntersectionObserverContainer = forwardRef(
  (
    {
      children,
      callback,
      rootMargin,
      threshold,
    }: IntersectionObserverContainerProps,
    forwardedRef: ForwardedRef<HTMLElement>,
  ) => {
    const callbackRef = useRef(callback);

    useLayoutEffect(() => {
      callbackRef.current = callback;

      return () => {
        callbackRef.current = noop;
      };
    }, [callback]);

    const [rootRef, childRef] = useForwardedRef(forwardedRef);

    const modifiedChildren = useMemo(
      () =>
        cloneElement(children, {
          ref: childRef,
        }),
      [childRef, children],
    );

    const handleCallback = useCallback(
      (entries: IntersectionObserverEntryWithPayload[]) => {
        callbackRef.current(entries);
      },
      [],
    );

    return (
      <IntersectionObserverProvider
        callback={handleCallback}
        root={rootRef}
        rootMargin={rootMargin}
        threshold={threshold}
      >
        {modifiedChildren}
      </IntersectionObserverProvider>
    );
  },
);

IntersectionObserverContainer.displayName = 'IntersectionObserverContainer';

/**
 * When using `IntersectionObserverContainer`, use this hook to observe changes to given element.
 *
 * @param targetRef Observer this element via intersection observer within context.
 *
 * @see IntersectionObserverContainer
 */
export function useIntersectionObserver(
  targetRef: RefObject<HTMLElement | null>,
  payload?: unknown,
) {
  const observer = useContext(IntersectionObserverContext);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  useLayoutEffect(() => {
    if (observer) {
      observer.observe(targetRef, payloadRef);

      return () => observer.unobserve(targetRef);
    }
  }, [observer, targetRef, payloadRef]);
}
